import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Box,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material'
import { StructuredQuestion } from '../interfaces/questions'

interface DeleteQuestionDialogProps {
  open: boolean
  question: StructuredQuestion | null
  allQuestions: StructuredQuestion[]
  onConfirm: (remapTo: string | null) => void
  onClose: () => void
}

export default function DeleteQuestionDialog({
  open,
  question,
  allQuestions,
  onConfirm,
  onClose,
}: DeleteQuestionDialogProps) {
  const [choice, setChoice] = useState<'clear' | 'remap'>('clear')
  const [remapTarget, setRemapTarget] = useState('')

  const deletedNumber = question?.question_number ?? ''

  // Find questions that reference the one being deleted
  const dependents = useMemo(() => {
    if (!deletedNumber) return []
    return allQuestions.filter((q) => {
      if (q.question_number === deletedNumber) return false
      const conditionalMatch =
        q.conditional_on?.question_number === deletedNumber
      const dependsOnMatch =
        q.depends_on?.question_numbers?.includes(deletedNumber) ?? false
      return conditionalMatch || dependsOnMatch
    })
  }, [allQuestions, deletedNumber])

  // Other questions available as remap targets (excluding deleted + its dependents)
  const dependentNumbers = useMemo(
    () => new Set(dependents.map((d) => d.question_number)),
    [dependents],
  )
  const remapOptions = useMemo(
    () =>
      allQuestions.filter(
        (q) =>
          q.question_number !== deletedNumber &&
          !dependentNumbers.has(q.question_number),
      ),
    [allQuestions, deletedNumber, dependentNumbers],
  )

  const handleConfirm = () => {
    const target = choice === 'remap' && remapTarget ? remapTarget : null
    onConfirm(target)
    setChoice('clear')
    setRemapTarget('')
  }

  const handleClose = () => {
    setChoice('clear')
    setRemapTarget('')
    onClose()
  }

  // No dependents — no dialog needed, but handle gracefully
  if (!question) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete question {deletedNumber}?</DialogTitle>
      <DialogContent>
        {question.question && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Question: {question.question.slice(0, 120)}
            {question.question.length > 120 ? '...' : ''}
          </Typography>
        )}
        {dependents.length === 0 ? (
          <Typography variant="body2">
            No other questions reference this question. It can be safely
            deleted.
          </Typography>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>
                {dependents.length}{' '}
                {dependents.length === 1
                  ? 'question depends'
                  : 'questions depend'}{' '}
                on this question
              </AlertTitle>
              {dependents.map((dep) => (
                <Box key={dep.question_number} sx={{ ml: 1, mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Q{dep.question_number}</strong>
                    {dep.question
                      ? ` — ${dep.question.slice(0, 60)}${
                          dep.question.length > 60 ? '...' : ''
                        }`
                      : ''}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {dep.conditional_on?.question_number === deletedNumber &&
                      'Conditional on this question'}
                    {dep.conditional_on?.question_number === deletedNumber &&
                      dep.depends_on?.question_numbers?.includes(
                        deletedNumber,
                      ) &&
                      ', '}
                    {dep.depends_on?.question_numbers?.includes(
                      deletedNumber,
                    ) && 'Depends on this question'}
                  </Typography>
                </Box>
              ))}
            </Alert>
            <FormControl sx={{ width: '100%' }}>
              <RadioGroup
                value={choice}
                onChange={(e) => setChoice(e.target.value as 'clear' | 'remap')}
              >
                <FormControlLabel
                  value="clear"
                  control={<Radio />}
                  label={
                    <Typography variant="body2">
                      Clear references — remove the dependency / conditional
                      from affected questions
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="remap"
                  control={<Radio />}
                  label={
                    <Typography variant="body2">
                      Remap references to another question
                    </Typography>
                  }
                />
              </RadioGroup>
              {choice === 'remap' && (
                <FormControl size="small" sx={{ mt: 1, ml: 4 }}>
                  <InputLabel>Remap to</InputLabel>
                  <Select
                    value={remapTarget}
                    label="Remap to"
                    onChange={(e) => setRemapTarget(e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {remapOptions.map((q) => (
                      <MenuItem
                        key={q.question_number}
                        value={q.question_number}
                      >
                        Q{q.question_number}
                        {q.question
                          ? ` — ${q.question.slice(0, 50)}${
                              q.question.length > 50 ? '...' : ''
                            }`
                          : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={choice === 'remap' && !remapTarget}
        >
          Delete Question
        </Button>
      </DialogActions>
    </Dialog>
  )
}
